// The "Save to list" dialog's rules, kept pure so they are unit-tested apart from the
// component (OnBoardingAddToListPage.vue) and match the iOS and Android sheets:
//
// - Completed and Favourites first, then the user's lists in the order the server sent
//   them. The order never changes while the dialog is open.
// - Search is case- and accent-insensitive ("alp" finds "Álpine lakes").
// - The Added filter keeps a list in view after the hike is taken out of it (`keepIds`),
//   so a row doesn't vanish from under the pointer; switching filter or searching drops
//   those.
// - After Done the page says what changed: "Saved to Favourites and Hiker list".

import { COMPLETED_LIST_KIND, HikeListLike, isHikeInList } from './hikeListMembership';

export const FAVOURITES_LIST_KIND = 'FAVOURITES';

export type SaveToListFilter = 'all' | 'added';

export interface SaveToListList extends HikeListLike {
  name: string;
  publiclyVisible?: boolean | null;
  images?: Array<{ file?: { url?: string | null; smUrl?: string | null } | null } | null> | null;
}

/** Completed, then Favourites, then everything else in the order given. */
export function orderForSaveToList<T extends SaveToListList>(lists: T[]): T[] {
  const rank = (list: T) => (list.kind === COMPLETED_LIST_KIND ? 0 : list.kind === FAVOURITES_LIST_KIND ? 1 : 2);
  return lists
    .map((list, index) => ({ list, index }))
    .sort((a, b) => rank(a.list) - rank(b.list) || a.index - b.index)
    .map(({ list }) => list);
}

/** Lower-cased, accents stripped, for search matching. */
export function foldForSearch(text: string): string {
  return String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function matchesQuery(name: string, query: string): boolean {
  const q = foldForSearch(query.trim());
  return q === '' || foldForSearch(name).includes(q);
}

/** Only lists the user made can be deleted; Completed and Favourites can't. */
export function isDeletableList(list: SaveToListList): boolean {
  return !list.kind;
}

export interface SaveToListView<T> {
  rows: T[];
  /** Lists the hike is in, for the "Added N" pill. */
  addedCount: number;
  /** The small heading above the rows; null while searching. */
  heading: 'your_lists' | 'added_to' | null;
  /** "New list" at the top (All, no search). */
  newListAtTop: boolean;
  /** "New list “<query>”" under the results while searching. */
  newListAfterResults: string | null;
  /** The "N lists match" / "No lists match" line while searching. */
  matchCount: number | null;
  /** "This hike isn't in any of your lists yet." */
  showsAddedEmpty: boolean;
}

export function saveToListView<T extends SaveToListList>({
  lists,
  hikeId,
  filter,
  query,
  keepIds = [],
}: {
  lists: T[];
  hikeId: string | number | null | undefined;
  filter: SaveToListFilter;
  query: string;
  keepIds?: Array<string | number>;
}): SaveToListView<T> {
  const q = query.trim();
  const kept = new Set(keepIds.map(String));
  const pool = filter === 'added'
    ? lists.filter((list) => isHikeInList(list, hikeId) || kept.has(String(list.id)))
    : lists;
  const rows = q ? pool.filter((list) => matchesQuery(list.name, q)) : pool;
  return {
    rows,
    addedCount: lists.filter((list) => isHikeInList(list, hikeId)).length,
    heading: q ? null : filter === 'added' ? 'added_to' : 'your_lists',
    newListAtTop: !q && filter === 'all',
    newListAfterResults: q || null,
    matchCount: q ? rows.length : null,
    showsAddedEmpty: !q && filter === 'added' && rows.length === 0,
  };
}

export type ListThumbnail =
  | { kind: 'completed' }
  | { kind: 'favourites' }
  | { kind: 'photo'; url: string }
  | { kind: 'placeholder' };

/** Completed and Favourites get their icon; a user's list its newest photo, else a placeholder. */
export function thumbnailFor(list: SaveToListList): ListThumbnail {
  if (list.kind === COMPLETED_LIST_KIND) return { kind: 'completed' };
  if (list.kind === FAVOURITES_LIST_KIND) return { kind: 'favourites' };
  for (const image of list.images ?? []) {
    const url = image?.file?.smUrl || image?.file?.url;
    if (url) return { kind: 'photo', url };
  }
  return { kind: 'placeholder' };
}

/** "No hikes yet" / "1 hike" / "7 hikes", as a message key and count. */
export function hikeCountLabel(count: number | null | undefined): { key: 'none' | 'one' | 'many'; count: number } {
  const n = Math.max(0, Number(count) || 0);
  return { key: n === 0 ? 'none' : n === 1 ? 'one' : 'many', count: n };
}

/** Whether the hike was in each list when the dialog opened. */
export function membershipSnapshot(lists: SaveToListList[], hikeId: string | number | null | undefined): Record<string, boolean> {
  return Object.fromEntries(lists.map((list) => [String(list.id), isHikeInList(list, hikeId)]));
}

/**
 * The lists the hike was added to and taken out of since the dialog opened, in the dialog's
 * order. A list made in the dialog counts as added; a deleted one is in neither.
 */
export function sessionChanges(
  initial: Record<string, boolean>,
  lists: SaveToListList[],
  hikeId: string | number | null | undefined,
): { added: string[]; removed: string[] } {
  const added: string[] = [];
  const removed: string[] = [];
  for (const list of lists) {
    const was = initial[String(list.id)] ?? false;
    const now = isHikeInList(list, hikeId);
    if (now && !was) added.push(list.name);
    if (was && !now) removed.push(list.name);
  }
  return { added, removed };
}

/** How the note names the lists: "A", "A and B", or "A and 2 more". */
export type ListNames =
  | { key: 'one'; first: string }
  | { key: 'two'; first: string; second: string }
  | { key: 'more'; first: string; count: number };

export function listNames(names: string[]): ListNames | null {
  if (names.length === 0) return null;
  if (names.length === 1) return { key: 'one', first: names[0] };
  if (names.length === 2) return { key: 'two', first: names[0], second: names[1] };
  return { key: 'more', first: names[0], count: names.length - 1 };
}

/** The note shown on the page after Done: saves win over removals; nothing changed, no note. */
export function doneNote(changes: { added: string[]; removed: string[] }): { kind: 'saved' | 'removed'; names: ListNames } | null {
  const saved = listNames(changes.added);
  if (saved) return { kind: 'saved', names: saved };
  const removed = listNames(changes.removed);
  if (removed) return { kind: 'removed', names: removed };
  return null;
}

/** Takes a list out, remembering where it was so Undo can put it back in the same place. */
export function withoutList<T extends SaveToListList>(lists: T[], listId: string | number): { lists: T[]; removed: T | null; index: number } {
  const index = lists.findIndex((list) => String(list.id) === String(listId));
  if (index < 0) return { lists, removed: null, index: -1 };
  return { lists: [...lists.slice(0, index), ...lists.slice(index + 1)], removed: lists[index], index };
}

export function withListAt<T extends SaveToListList>(lists: T[], list: T, index: number): T[] {
  if (lists.some((existing) => String(existing.id) === String(list.id))) return lists;
  const at = Math.min(Math.max(0, index), lists.length);
  return [...lists.slice(0, at), list, ...lists.slice(at)];
}

/** Where a new list goes: after Completed and Favourites, ahead of the user's other lists. */
export function withNewList<T extends SaveToListList>(lists: T[], list: T): T[] {
  const firstOwn = lists.findIndex((existing) => !existing.kind);
  return withListAt(lists, list, firstOwn < 0 ? lists.length : firstOwn);
}

/** A fresh client id for a new list photo, the same shape as My Lists' (users/hike_lists). */
export function newImageClientId(): string {
  const uuid = (globalThis as any).crypto?.randomUUID?.();
  return uuid || `img_${Math.random().toString(36).slice(2, 10)}`;
}

export interface HikeListImageAttributes {
  clientId: string;
  signedId: string;
  isCover: boolean;
}

/**
 * The new list's `imagesAttributes` for its uploaded photos. Every photo needs its own
 * `clientId`: ImageAttributesType requires it, and createHikeList finds each saved image by
 * it to attach the upload. Without one the whole create was refused. The first photo is the
 * cover, as on My Lists.
 */
export function hikeListImagesAttributes(signedIds: string[], newClientId: () => string = newImageClientId): HikeListImageAttributes[] {
  return signedIds.map((signedId, index) => ({ clientId: newClientId(), signedId, isCover: index === 0 }));
}

/**
 * What the dialog says when a request fails. A reason the server wrote for people (a
 * mutation's `errors`, thrown as a plain Error) is shown as it is. graphql-request's own
 * ClientError — anything with a `response` — has a message that is the server's text plus a
 * JSON dump of the query and variables, so that, and anything empty, gets [fallback].
 */
export function saveToListErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object' || 'response' in error) return fallback;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
}

export const HIKE_LISTS_PAGE_SIZE = 20;

/**
 * Every page of the user's lists. userHikeLists caps a page at 20, which is why the old
 * modal never showed a 21st list. Stops at the first short page (or `maxPages`).
 */
export async function fetchAllHikeListPages<T>(
  fetchPage: (page: number, perPage: number) => Promise<T[]>,
  { perPage = HIKE_LISTS_PAGE_SIZE, maxPages = 50 }: { perPage?: number; maxPages?: number } = {},
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const rows = (await fetchPage(page, perPage)) ?? [];
    all.push(...rows);
    if (rows.length < perPage) break;
  }
  return all;
}
